/**
 * @file Utilities for handling Transfermarkt URLs
 * @remarks Transfermarkt has 27+ international domains including .com, .de, .co.uk,
 * .us, .fr, .es, .it, .pl, .at, .nl, .pt, .com.tr, .com.br, .co.id, etc.
 * The regex pattern is designed to be flexible and match any TLD format.
 */

/**
 * Regex pattern for matching Transfermarkt player profile URLs.
 * Supports all TLD formats including:
 * - Simple TLDs: .com, .de, .us, .fr, .es, .it, .pl, .at, .nl, .pt
 * - Compound TLDs: .co.uk, .co.id, .com.tr, .com.br, .com.ar
 * @remarks The pattern (?:\.[a-z]+)? allows optional second TLD segment.
 * Captures the numeric player ID (the trailing path segment after /spieler/).
 */
const TRANSFERMARKT_PLAYER_URL_PATTERN =
  /^https?:\/\/(?:www\.)?transfermarkt\.[a-z]+(?:\.[a-z]+)?\/[^/]+\/profil\/spieler\/(\d+)/i;

/**
 * GraphQL Int is a 32-bit signed integer; IDs above this cannot be sent to
 * the getPlayerByTransfermarktId query.
 */
const MAX_GRAPHQL_INT = 2_147_483_647;

/**
 * Checks if a given text is a valid Transfermarkt player profile URL
 * @param text - The input text to validate
 * @returns True if the text is a valid Transfermarkt player URL, false otherwise
 * @example
 * isTransfermarktUrl("https://www.transfermarkt.com/lionel-messi/profil/spieler/28003") // true
 * isTransfermarktUrl("https://www.transfermarkt.co.uk/lionel-messi/profil/spieler/28003") // true
 * isTransfermarktUrl("https://www.google.com") // false
 */
export function isTransfermarktUrl(text: string): boolean {
  const trimmed = text.trim();
  return TRANSFERMARKT_PLAYER_URL_PATTERN.test(trimmed);
}

/**
 * Parses a Transfermarkt player profile URL and extracts the numeric player ID
 * @param text - The input text containing a Transfermarkt URL
 * @returns The Transfermarkt player ID, or null if the text is not a valid
 * Transfermarkt player URL or the ID exceeds the GraphQL Int range
 * @example
 * parseTransfermarktId("https://www.transfermarkt.com/lionel-messi/profil/spieler/28003")
 * // Returns: 28003
 *
 * parseTransfermarktId("https://www.google.com")
 * // Returns: null
 */
export function parseTransfermarktId(text: string): number | null {
  const trimmed = text.trim();
  const match = TRANSFERMARKT_PLAYER_URL_PATTERN.exec(trimmed);

  if (!match) {
    return null;
  }

  const id = Number.parseInt(match[1], 10);
  return Number.isSafeInteger(id) && id > 0 && id <= MAX_GRAPHQL_INT
    ? id
    : null;
}
